// Parametric geometry engine — calculates real cross-section shapes
// for every forging station from first principles

export interface Geo {
  stn: number; label: string; type: 'BOLT'|'NUT'|'SETSCREW';
  // Bolt
  wireD?: number; blankL?: number;
  headD?: number; headH?: number;
  shankD?: number; shankL?: number;
  socketAF?: number; socketDepth?: number;
  hasPoint?: boolean; pointL?: number;
  hasFlange?: boolean; flangeD?: number; flangeH?: number;
  hasSerration?: boolean; hasDome?: boolean;
  hasTopNib?: boolean; hasTrimDie?: boolean;
  coneAngle?: number; neckdown?: boolean;
  isCutoff?: boolean;
  // Nut
  nutAF?: number; nutH?: number;
  boreD?: number; isHex?: boolean;
  activeKO?: boolean; isPierced?: boolean;
  topChamfer?: number; botChamfer?: number;
  // Set screw
  bodyD?: number; bodyL?: number;
  topCone?: number; botCone?: number;
  hasCupPoint?: boolean; hasStripper?: boolean;
  // Die / KO
  dieAngle?: number; koType?: string; punchMat?: string; palletStr?: string;
  // FEA strain
  strainMax?: number; strainMin?: number;
}

export function boltGeo(part: any): Geo[] {
  const d  = part.nominal_diameter_mm as number;
  const w  = (part.wire_diameter_mm  ?? d * 1.05) as number;
  const bl = (part.blank_length_mm   ?? part.total_length_mm * 1.02) as number;
  const sk = (part.shank_length_mm   ?? bl * 0.72) as number;
  const ld = bl / w;
  const isB  = ld < 3.5;
  const isC  = part.head_type === 'NONE';
  const isSr = part.has_serration;
  const tpl  = isC ? 'C' : isB ? (isSr ? 'B_S' : 'B') : 'A';

  if (isC) return setscrewGeo(d, w, bl);

  const finalHeadD  = (part.has_flange ? d * 1.90 : d * 1.73);
  const finalHeadH  = isB ? d * 0.72 : d * 0.66;
  const flangeD     = finalHeadD;
  const flangeH     = isB ? finalHeadH * 0.38 : 0;
  const socketAF    = d * 0.63;
  const socketDepth = finalHeadH * 0.60;
  const shankD      = d * 0.975;
  const coneAngle   = isB ? 9 : (w >= 11 ? 9 : 6);
  const hasSock     = part.socket_type === 'HEX_SOCKET' || !part.head_type || part.head_type?.includes('SOCKET');

  const geo: Geo[] = [];

  // Station 0 — cut-off blank
  geo.push({ stn:0, label:'CUT-OFF', type:'BOLT', isCutoff:true,
    wireD:w, blankL:bl, headD:w, headH:bl, shankD:w, shankL:bl,
    strainMax:0, strainMin:0, dieAngle:0, koType:'—', punchMat:'—', palletStr:'—' });

  if (isB) {
    geo.push({ stn:1, label:'STN 1', type:'BOLT',
      headD: w*1.27, headH: finalHeadH*0.52,
      shankD: w, shankL: bl*0.6,
      hasFlange: part.has_flange, flangeD: w*1.27, flangeH: flangeH*0.4,
      coneAngle:9, hasTopNib:false,
      dieAngle:35, koType:'Plain', punchMat:'D2', palletStr:'Carbide Ø25×50',
      strainMax:0.30, strainMin:0.04 });
    geo.push({ stn:2, label:'STN 2', type:'BOLT',
      headD: finalHeadD*0.94, headH: finalHeadH*0.88,
      shankD: shankD*1.01, shankL: bl*0.55,
      hasFlange: part.has_flange, flangeD: finalHeadD*0.94, flangeH: flangeH*0.85,
      hasTopNib:true,
      dieAngle:30, koType:'Plain', punchMat:'D2', palletStr:'Carbide Ø25×50',
      strainMax:0.55, strainMin:0.08 });
    geo.push({ stn:3, label:'STN 3', type:'BOLT',
      headD: finalHeadD*0.99, headH: finalHeadH*0.97,
      shankD: shankD, shankL: sk*1.05,
      hasFlange: part.has_flange, flangeD: finalHeadD*0.99, flangeH: flangeH*0.96,
      hasSerration: isSr,
      socketAF, socketDepth: socketDepth*0.95,
      dieAngle:30, koType:'Plain', punchMat:'D2', palletStr:'Steel Ø35×35',
      strainMax:0.72, strainMin:0.11 });
    geo.push({ stn:4, label:'STN 4', type:'BOLT',
      headD: finalHeadD, headH: finalHeadH,
      shankD: shankD, shankL: sk,
      hasFlange: part.has_flange, flangeD, flangeH,
      hasSerration: isSr, hasDome:true, hasTrimDie:true,
      socketAF, socketDepth,
      dieAngle:0, koType:'Plain', punchMat:'M2', palletStr:'Carbide Ø35×35',
      strainMax:0.85, strainMin:0.13 });
  } else {
    // Template A
    geo.push({ stn:1, label:'STN 1', type:'BOLT',
      headD:w, headH:w*0.42, shankD:w, shankL:bl, coneAngle,
      dieAngle:coneAngle, koType:'Plain', punchMat:'D2', palletStr:'Carbide Ø25×50',
      strainMax:0.09, strainMin:0.01 });
    geo.push({ stn:2, label:'STN 2', type:'BOLT',
      headD: finalHeadD*0.73, headH: finalHeadH*0.62,
      shankD: w*0.94, shankL: bl*0.86, hasTopNib: part.has_flange,
      dieAngle:35, koType:'Plain', punchMat:'D2', palletStr:'Carbide Ø25×50',
      strainMax:0.40, strainMin:0.10 });
    geo.push({ stn:3, label:'STN 3', type:'BOLT',
      headD: finalHeadD*0.97, headH: finalHeadH*0.94,
      shankD: shankD*1.01, shankL: bl*0.80,
      socketAF: hasSock ? socketAF : undefined,
      socketDepth: hasSock ? socketDepth*0.95 : undefined,
      hasPoint:true, pointL:4,
      dieAngle:30, koType:'Plain',
      punchMat: hasSock ? (socketAF <= 7 ? 'M42' : 'M2') : 'D2',
      palletStr:'Carbide Ø25×40',
      strainMax:0.64, strainMin:0.14 });
    geo.push({ stn:4, label:'STN 4', type:'BOLT',
      headD: finalHeadD, headH: finalHeadH,
      shankD, shankL: sk,
      socketAF: hasSock ? socketAF : undefined,
      socketDepth: hasSock ? socketDepth : undefined,
      hasPoint:true, pointL:4, hasTrimDie:true,
      dieAngle:0, koType:'Plain', punchMat:'M2', palletStr:'Carbide Ø35×35',
      strainMax:0.80, strainMin:0.16 });
  }
  return geo;
}

function setscrewGeo(d: number, w: number, bl: number): Geo[] {
  return [
    { stn:0, label:'CUT-OFF', type:'SETSCREW', isCutoff:true, bodyD:w, bodyL:bl, strainMax:0, strainMin:0 },
    { stn:1, label:'STN 1',   type:'SETSCREW', bodyD:w, bodyL:bl*0.99, topCone:60, botCone:45,
      dieAngle:0, koType:'None (stripper)', punchMat:'M42', palletStr:`Carbide ${d<=10?'Ø20×40':'Ø25×40'}`, strainMax:0.30, strainMin:0.05 },
    { stn:2, label:'STN 2',   type:'SETSCREW', bodyD:w, bodyL:bl*0.99,
      topCone:60, socketAF:d*0.63, socketDepth:bl*0.22,
      hasCupPoint:true, hasStripper:true,
      dieAngle:0, koType:'None (stripper)', punchMat:'M42', palletStr:`Carbide ${d<=10?'Ø20×40':'Ø25×40'}`, strainMax:0.64, strainMin:0.10 },
  ];
}

export function nutGeo(part: any): Geo[] {
  const d     = part.nominal_diameter_mm as number;
  const af    = (part.nut_af_mm    ?? d * 1.7)  as number;
  const h     = (part.nut_height_mm ?? d * 0.8) as number;
  const wire  = (part.wire_diameter_mm ?? af * 0.68) as number;
  const isD1  = part.has_flange || part.has_serration;
  const hexStn = isD1 ? 3 : 2;
  const sb    = 0.13; // springback allowance

  const geo: Geo[] = [];
  geo.push({ stn:0, label:'BLANK', type:'NUT', isCutoff:true,
    nutAF:wire, nutH:wire*0.62, isHex:false, boreD:0,
    strainMax:0, strainMin:0, koType:'—' });

  for (let i = 1; i <= 6; i++) {
    const prog   = i / 5;
    const isHex  = i >= hexStn;
    const isAct  = i >= 4;
    const isPierc= i === 6;
    const stAF   = isPierc ? af - sb : wire + (af - sb - wire) * Math.min(prog * 1.12, 1);
    const stH    = Math.max(wire * 0.52, h * (0.54 + prog * 0.46));
    const boreD  = isPierc ? (d * 0.84 - 0.15) : 0;
    const dieAng = i === 1 ? 35 : i <= 4 ? 30 : 5;

    geo.push({
      stn:i, label:`STN ${i}`, type:'NUT',
      nutAF: stAF, nutH: stH, boreD, isHex,
      topChamfer: 1.2, botChamfer: isAct ? 0.8 : 0,
      activeKO: isAct, isPierced: isPierc,
      dieAngle: dieAng, koType: isPierc ? 'None (free-fall)' : isAct ? 'Active ▲' : 'Plain',
      punchMat:'M2', palletStr:'Carbide (GT-55)',
      strainMax: 0.10 + prog * 0.65, strainMin: 0.02 + prog * 0.08,
    });
  }
  return geo;
}
